<template>
    <div class="plant-name">
        <div class="title" :class="{ 'compare-title': props.comparisonMode }">
            <h1>{{ plantTitle }}</h1>
            <span v-if="props.relatedStoretrackDetails && !props.comparisonMode" class="storetrack-id">Storage project ID: {{ props.relatedStoretrackDetails.factStorageId }}</span>
        </div>
        <icofont-icon v-if="props.comparisonMode" :icon="technologyIconToDisplay" :background-colour="technologyColourToDisplay" />
        <div class="plant-title-right" v-if="!props.comparisonMode">
            <div class="button-holder" v-if="props.relatedStoretrackDetails">
                <primevue-button class="plain-btn" @click="toggleTimeline">
                    <div class="button-content">Updates Timeline</div>
                </primevue-button>
                <primevue-button :disabled="relatedArticles.length === 0" class="plain-btn" @click="toggleNews">
                    <div class="button-content">Project News</div>
                </primevue-button>
                <popover ref="timelinePopover">
                    <div>
                        <updates-timeline-popover :source-date-list="props.relatedStoretrackDetails.sourceDates" />
                    </div>
                </popover>
                <popover ref="newsPopover">
                    <div>
                        <news-popoverr :articles="relatedArticles" @open-project-in-modal="openProjectInModal" />
                    </div>
                </popover>
            </div>
            <icofont-icon :icon="technologyIconToDisplay" :background-colour="technologyColourToDisplay" size="extra-large" />
            <div class="flag" v-if="props.relatedStoretrackDetails">
                <country-flag :iso-code="props.relatedStoretrackDetails.country.toLowerCase()" />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import PrimevueButton from 'primevue/button';
import Popover from 'primevue/popover';

import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { IPlantDetails } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import UpdatesTimelinePopover from '@/components/storageTracking/projectSearch/UpdatesTimelinePopover.vue';
import NewsPopoverr from '@/components/storageTracking/projectSearch/NewsPopover.vue';
import CountryFlag from '@/components/powerMarkets/shared/CountryFlag.vue';
import { articleScrapingApi } from '@/api/storageTracking/ArticleScrapingApi.ts';
import { IArticle } from '@/interfaces/storageTracking/IArticle.ts';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';

const toast = useToast();
const relatedArticles = ref<IArticle[]>([]);

const props = withDefaults(
    defineProps<{
        plantInfo?: IPlantDetails | null;
        comparisonMode?: boolean;
        relatedStoretrackDetails?: ICsvData | null;
        basePlantName?: string | null;
        modelValue?: string | null;
    }>(),
    {
        comparisonMode: false,
        relatedStoretrackDetails: undefined,
        plantInfo: undefined,
        basePlantName: undefined,
        modelValue: undefined,
    },
);

const emit = defineEmits<{
    'open-project-in-modal': [IRedactedProjectData];
    'update:modelValue': [string];

}>();

const timelinePopover = ref<InstanceType<typeof Popover> | null>(null);
const newsPopover = ref<InstanceType<typeof Popover> | null>(null);

const plantTitle = computed<string>(() => {
    if (props.comparisonMode) return props.plantInfo ? `${props.plantInfo.id} - ${props.plantInfo.name}` : '';
    if (props.relatedStoretrackDetails) return props.relatedStoretrackDetails.projectName;
    if (props.basePlantName) return props.basePlantName;
    if (props.plantInfo) {
        if (props.plantInfo.name === '') return props.plantInfo.id;
        return props.plantInfo.name;
    }
    return 'No name found';
});

watch(() => plantTitle.value, (newTitle) => {
    emit('update:modelValue', newTitle);
}, { immediate: true });

const technologyIconToDisplay = computed<string>(() => {
    if (props.plantInfo) return props.plantInfo.fuelIcon;
    if (props.relatedStoretrackDetails?.technology.includes('Pumped hydro storage')) return 'water-drop';
    return 'battery-half';
});

const technologyColourToDisplay = computed<string>(() => {
    if (props.plantInfo) return props.plantInfo.colour;
    return 'var(--sparkOrange)';
});

function toggleTimeline (event: MouseEvent): void {
    if (!timelinePopover.value) return;
    timelinePopover.value.toggle(event);
}

function toggleNews (event: MouseEvent): void {
    if (!newsPopover.value) return;
    newsPopover.value.toggle(event);
}

async function getScrapedArticles (storetrackPlant: ICsvData): Promise<void> {
    articleScrapingApi.getArticlesForFactStorageId(storetrackPlant.factStorageId?.toString()).then((response: IArticle[]) => {
        relatedArticles.value = response;
    }).catch((error) => {
        publishToast(toast, error, 'error');
    });
}

function openProjectInModal (project: IRedactedProjectData): void {
    emit('open-project-in-modal', project);
    if (!newsPopover.value) return;
    newsPopover.value.hide();
}

watch(() => props.relatedStoretrackDetails, async () => {
    if (!props.relatedStoretrackDetails) {
        relatedArticles.value = [];
        return;
    }
    getScrapedArticles(props.relatedStoretrackDetails);
}, { immediate: true });

</script>

<style lang="scss" scoped>
.plant-name {
    display: flex;
    font-size: var(--extraLargeFontSize);
    gap: var(--defaultSpacing);
    justify-content: space-between;
    margin-bottom: var(--defaultSpacing);
    align-items: center;
    @include responsive('sm') {
        flex-direction: column;
    }
}
.title {
    display: flex;
    flex-direction: column;
    justify-content: start;

    &.compare-title {
        h1 {
            font-size: var(--mediumFontSize);
        }
    }
    .storetrack-id {
        font-size: var(--smallFontSize);
        font-weight: var(--lightFontWeight);
        margin-top: var(--smallSpacing);
    }
}

.plant-title-right {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: var(--defaultSpacing);
        height: fit-content;

        .button-holder {
            font-size: var(--smallFontSize);
            display: flex;
            flex-direction: column;
            gap: var(--smallSpacing);
        }

        .flag {
            width: 35px;
            height: 35px;
            place-self: center;
            grid-area: flag;
        }
    }
</style>
